import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {Trans, useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {CaretDown, CaretRight, MagnifyingGlass, Plus, X} from '../../icons';
import {Spinner} from '../../Spinner';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {isOk} from '../../../utils/apiResponse';
import {AlertRulesApi} from '../../../api/alertRulesApi';
import {
    ERROR_NOT_FOUND,
    errorFromResponse,
    FILTER_ALL,
    FILTER_LABEL_KEYS,
    FILTER_MINE,
    filterFromParam,
    insertRuleIn,
    isConverted,
    isCustom,
    limitReached,
    matchesRuleParam,
    normalizeSearch,
    PAUSED_LIMIT,
    removeRuleFrom,
    replaceRuleIn,
    ruleAnchorId,
    RULE_FILTERS,
    ruleUse,
    rulesView,
    splitRules,
    USE_EMAIL,
    USE_NOTICE,
    USE_PAINT,
    USE_PAUSED,
} from '../rulesModel';
import {
    customEffects,
    emailCanReachViewer,
    emailSummary,
    SECTION_ORDER,
    systemEffects,
    systemSavePayload,
} from '../systemRulesModel';
import {customRuleSearchText, systemRuleSearchText, systemRuleTitle} from '../ruleText';
import {ALERTS_NEW, COMPARATIVE_METRICS_PATH, PARAM_FILTER, PARAM_RULE} from '../alertsCenterPaths';
import {ChannelsCard, WEBHOOK_OK} from './ChannelsCard';
import {ConvertedRulesList} from './ConvertedRulesList';
import {CustomRuleRow, SystemRuleRow} from './RuleRow';
import {SystemRulePanel} from './SystemRulePanel';
import {WebhookCardV2} from './WebhookCardV2';
import {dismissRuleUndoToast, showRuleUndoToast} from './ruleUndoToast';

const HIGHLIGHT_MS = 2500;

// Lo que «Todas» muestra plegado (C-06): las de Dashbo que no mandan correo. Nada se apaga ni se
// pierde: cada pliegue se abre con un clic, con su filtro o con el link de la tuerca del Tablero.
const FOLDS = [
    {key: 'notice', use: USE_NOTICE},
    {key: 'paint', use: USE_PAINT},
    {key: 'paused', use: USE_PAUSED},
];
const FOLD_BY_USE = {[USE_NOTICE]: 'notice', [USE_PAINT]: 'paint', [USE_PAUSED]: 'paused'};
const FOLD_CANNOT = 'cannot';

// Dentro de un grupo, las de Dashbo en el orden de sus secciones (Presupuesto, Conversiones…).
const sectionRank = (rule) => {
    const index = SECTION_ORDER.indexOf(rule.section);
    return index === -1 ? SECTION_ORDER.length : index;
};
const bySection = (rules) => [...rules].sort((a, b) => sectionRank(a) - sectionRank(b));

/**
 * «Mis reglas» (`/alerts/rules`): las reglas de Dashbo y las personalizadas en UNA lista (D1), con
 * «Adónde llegan los avisos» arriba y el webhook al pie.
 *
 * - «Todas» (sin filtro ni búsqueda) muestra lo que avisa (recorrido con Pablo, 13/10, C-06): «Tus
 *   alertas» y «Te avisan por correo»; el resto de las de Dashbo va plegado por lo que hace («Dejan
 *   aviso, sin correo», «Sólo pintan el Tablero», «En pausa»). Es sólo presentación: ninguna se apaga.
 *   Con un filtro o una búsqueda, la lista de siempre, por sección.
 * - Qué hace cada regla (`ruleUse`) sale del rótulo «Correo» de su fila, así que el filtro «Mandan
 *   correo», el grupo «Te avisan por correo» y el «Correo: recibes N de M» de arriba cuentan lo mismo.
 * - El filtro vive en la URL (`?filtro=`): se comparte y «atrás» lo deshace. Un valor que no existe
 *   abre «Todas» y la URL se corrige.
 * - `?regla=<id>` (la tuerca del Tablero, el link de un correo, una regla recién guardada) limpia lo
 *   que la pudiera esconder, despliega su pliegue, la trae a la vista y la resalta; si es de Dashbo,
 *   abre su panel.
 * - Las que no pueden alertar (sin período de comparación) van aparte y plegadas, con el motivo y la
 *   salida.
 * - Eliminar no pide confirmación: se deshace 5 s desde el aviso (y el borrado es soft).
 * - Reanudar con la agencia en el tope de reglas activas no llega a pedirse: lo dice antes.
 * - Una regla que ya no existe (otra pestaña la borró) sale de la lista con su aviso, en vez de un
 *   «vuelve a intentarlo» que no sirve nunca.
 * - `canEdit` en false (quien mira no puede editar las alertas de la agencia): todo se lee y nada se
 *   cambia; los interruptores y las acciones que escriben no se ofrecen.
 *
 * Centro de Alertas v2 (README v2 §4): con `system_alerts_converted: true` las de Dashbo son filas como
 * las propias y la pantalla es otra (`ConvertedRulesList`). Con `false`, o con un payload viejo sin la
 * clave, esta de siempre, sin cambios: el FE se deploya antes de convertir a nadie. Mientras la lista no
 * llegó, se dibuja ésta (con su loader), que es lo que ve hoy toda agencia.
 */
export function RulesList(props) {
    if (isConverted(props.payload)) return <ConvertedRulesList {...props}/>;
    return <ComposedRulesList {...props}/>;
}

function ComposedRulesList({payload, loading, loadError, onRetry, onPayloadChange, userId, userEmail,
                               webhook, onWebhookSaved, onWebhookRetry, canEdit = true}) {
    const {t} = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const rawFilter = searchParams.get(PARAM_FILTER);
    const filter = filterFromParam(rawFilter);
    const ruleParam = searchParams.get(PARAM_RULE);
    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState({});
    const [panelRuleId, setPanelRuleId] = useState(null);
    const [highlightId, setHighlightId] = useState(null);
    const [openFolds, setOpenFolds] = useState(() => new Set());
    // La regla que pidió el deep link: su pliegue queda abierto aunque cambie de pliegue (el «Correo» de
    // quien mira llega en otro pedido y puede moverla), hasta que el usuario lo cierre.
    const [revealId, setRevealId] = useState(null);
    const highlightTimer = useRef(null);
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            clearTimeout(highlightTimer.current);
            // El «Deshacer» no sobrevive a la vista: su resultado ya no tendría dónde mostrarse. La
            // regla queda borrada (soft) y el backend la restaura dentro de las 24 h.
            dismissRuleUndoToast();
        };
    }, []);

    // Un `?filtro=` que no existe abre «Todas», y la URL tiene que decir lo mismo que la pantalla.
    useEffect(() => {
        if (rawFilter !== null && filterFromParam(rawFilter) !== rawFilter) {
            const next = new URLSearchParams(searchParams);
            next.delete(PARAM_FILTER);
            setSearchParams(next, {replace: true});
        }
    }, [rawFilter, searchParams, setSearchParams]);

    const split = useMemo(() => splitRules(payload), [payload]);
    const searchTextOf = useCallback((item) => (isCustom(item)
        ? customRuleSearchText(item) : systemRuleSearchText(item, t)), [t]);
    // El rótulo «Correo» de la fila, para quien mira: lo mismo que decide su filtro y su grupo.
    const emailOn = useCallback((item) => (isCustom(item)
        ? customEffects(item, {me: userId}).email.on
        : systemEffects(item, {userEmail}).email.on), [userId, userEmail]);
    const usageOf = useCallback((item) => ruleUse(item, emailOn), [emailOn]);
    const view = useMemo(() => rulesView(split, {filter, query, me: userId, searchTextOf, usageOf}),
        [split, filter, query, userId, searchTextOf, usageOf]);
    const needle = normalizeSearch(query);
    const grouped = filter === FILTER_ALL && !needle;
    // Las que no pueden alertar no entran en los filtros, pero el buscador también las encuentra: con
    // texto, sólo las que coinciden (review C-rp-fe-alertas-01).
    const cannotShown = useMemo(() => (needle
        ? split.cannotAlert.filter((item) => normalizeSearch(searchTextOf(item)).includes(needle))
        : split.cannotAlert), [split, needle, searchTextOf]);
    const cannotMatches = needle ? cannotShown.length : 0;

    // «Correo: recibes N de M»: N es lo que cuenta «Mandan correo» (sin la búsqueda); M, lo que podría
    // llegarle. Sin «Mis alertas» de quien mira, no se afirma un número.
    const emailCount = useMemo(() => {
        if (!payload || !emailSummary(userEmail)) return null;
        const all = [...split.custom, ...split.system];
        return {
            enabled: all.filter((item) => usageOf(item) === USE_EMAIL).length,
            total: all.filter((item) => emailCanReachViewer(item, userId)).length,
        };
    }, [payload, userEmail, split, usageOf, userId]);

    const webhookLive = webhook && webhook.status === WEBHOOK_OK
        ? {configured: Boolean(webhook.url), selected: webhook.selected || []} : null;
    const effectsOf = (item) => (isCustom(item)
        ? customEffects(item, {webhook: webhookLive, me: userId})
        : systemEffects(item, {userEmail, webhook: webhookLive}));

    // El pliegue de la regla del deep link, con el «Correo» de ahora.
    const revealFold = useMemo(() => {
        if (revealId === null) return null;
        if (split.cannotAlert.some((item) => String(item.id) === revealId)) return FOLD_CANNOT;
        const target = split.system.find((item) => String(item.id) === revealId);
        return target ? FOLD_BY_USE[usageOf(target)] || null : null;
    }, [revealId, split, usageOf]);
    const isFoldOpen = (key) => openFolds.has(key) || key === revealFold;
    const toggleFold = (key) => {
        const open = isFoldOpen(key);
        if (key === revealFold) setRevealId(null);
        setOpenFolds((current) => {
            const next = new Set(current);
            if (open) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    // Una búsqueda que encuentra algo en «No pueden alertar» abre ese pliegue: plegado, la coincidencia
    // quedaba escondida. Se puede volver a cerrar; una búsqueda nueva lo vuelve a abrir.
    useEffect(() => {
        if (cannotMatches === 0) return;
        setOpenFolds((current) => (current.has(FOLD_CANNOT) ? current : new Set([...current, FOLD_CANNOT])));
    }, [needle, cannotMatches]);

    const highlight = useCallback((id) => {
        clearTimeout(highlightTimer.current);
        setHighlightId(String(id));
        highlightTimer.current = setTimeout(() => {
            if (mounted.current) setHighlightId(null);
        }, HIGHLIGHT_MS);
    }, []);

    useEffect(() => {
        if (highlightId === null) return;
        const row = document.getElementById(ruleAnchorId(highlightId));
        if (row && row.scrollIntoView) row.scrollIntoView({block: 'center', behavior: 'smooth'});
    }, [highlightId]);

    // El deep link se atiende UNA vez por valor: cerrar el panel no lo vuelve a abrir.
    const handledParam = useRef(null);
    useEffect(() => {
        if (!ruleParam || !payload || handledParam.current === ruleParam) return;
        handledParam.current = ruleParam;
        const all = [...split.custom, ...split.system, ...split.cannotAlert];
        const target = all.find((item) => matchesRuleParam(item, ruleParam));
        if (!target) return;
        setQuery('');
        if (searchParams.get(PARAM_FILTER) !== null) {
            const next = new URLSearchParams(searchParams);
            next.delete(PARAM_FILTER);
            setSearchParams(next, {replace: true});
        }
        // En el mismo render que el resaltado: la fila tiene que existir cuando se la busca para scrollear.
        setRevealId(String(target.id));
        highlight(target.id);
        if (!isCustom(target) && target.can_alert !== false) setPanelRuleId(target.id);
    }, [ruleParam, payload, split, searchParams, setSearchParams, highlight]);

    const changeFilter = (next) => {
        const params = new URLSearchParams(searchParams);
        params.delete(PARAM_RULE);
        if (next === FILTER_ALL) params.delete(PARAM_FILTER);
        else params.set(PARAM_FILTER, next);
        setSearchParams(params);
    };

    const setRuleBusy = (id, value) => setBusy((current) => ({...current, [String(id)]: value}));
    const isBusy = (id) => Boolean(busy[String(id)]);

    // ------------------------------------------------------------------ acciones -----------------

    const limits = payload ? payload.limits : null;
    const maxActive = limits && typeof limits.max_active === 'number' ? limits.max_active : '';

    // La regla ya no existe (o ya no se ve): sale de la lista, con su aviso.
    const forgetRule = (rule) => {
        if (isCustom(rule)) onPayloadChange((current) => removeRuleFrom(current, rule.id));
        toast.error(t('alerts_center.rule_not_found'));
    };

    const toggle = (rule, next) => {
        if (isBusy(rule.id) || !canEdit) return;
        // El tope frena antes de pedir (C-ac-fe-centro-07): el backend lo rechazaría igual.
        if (isCustom(rule) && next && limitReached(limits)) {
            toast.error(t('alerts_center.builder.limit_reached', {max: maxActive}));
            return;
        }
        const previous = {...rule};
        setRuleBusy(rule.id, true);
        onPayloadChange((current) => replaceRuleIn(current, {...rule, is_active: next}));
        const request = isCustom(rule)
            ? AlertRulesApi.update(rule.id, {is_active: next})
            : AlertRulesApi.saveSystemRules(systemSavePayload(rule, null, next));
        request.then((response) => {
            if (!mounted.current) return;
            setRuleBusy(rule.id, false);
            if (!isOk(response)) {
                const error = errorFromResponse(response, {fallbackKey: 'alerts_center.rules.toggle_error'});
                if (error.code === ERROR_NOT_FOUND) {
                    forgetRule(rule);
                    return;
                }
                onPayloadChange((current) => replaceRuleIn(current, previous));
                toast.error(t(error.key, {max: maxActive}));
                return;
            }
            if (isCustom(rule)) {
                const saved = response.result && response.result.rule;
                if (saved) onPayloadChange((current) => replaceRuleIn(current, saved));
                toast.success(t(next ? 'alerts_center.rules.resumed_toast' : 'alerts_center.rules.paused_toast'));
            } else {
                toast.success(t('alerts_center.rules.saved_toast'));
            }
        });
    };

    const restore = (rule) => {
        AlertRulesApi.restore(rule.id).then((response) => {
            if (!isOk(response)) {
                toast.error(t('alerts_center.rules.restore_error'));
                return;
            }
            const restored = (response.result && response.result.rule) || {...rule, deleted_on: null};
            onPayloadChange((current) => insertRuleIn(current, restored));
            if (mounted.current) highlight(restored.id);
            // Con la agencia en el tope, vuelve en pausa: se dice (C-ac-fe-centro-07).
            if (restored.paused_reason === PAUSED_LIMIT) toast.warning(t('alerts_center.rules.restored_paused_limit'));
        });
    };

    const remove = (rule) => {
        if (isBusy(rule.id)) return;
        setRuleBusy(rule.id, true);
        AlertRulesApi.remove(rule.id).then((response) => {
            if (mounted.current) setRuleBusy(rule.id, false);
            if (!isOk(response)) {
                const error = errorFromResponse(response, {fallbackKey: 'alerts_center.rules.delete_error'});
                if (error.code === ERROR_NOT_FOUND) forgetRule(rule);
                else toast.error(t(error.key));
                return;
            }
            onPayloadChange((current) => removeRuleFrom(current, rule.id));
            showRuleUndoToast({
                message: t('alerts_center.rules.deleted_toast'),
                undoLabel: t('alerts_center.rules.undo'),
                onUndo: () => restore(rule),
                actionId: rule.id,
            });
        });
    };

    const closePanel = useCallback(() => setPanelRuleId(null), []);
    const panelRule = panelRuleId
        ? split.system.find((item) => String(item.id) === String(panelRuleId)) : null;

    const goToWebhook = useCallback(() => {
        const card = document.getElementById('webhook');
        if (!card) return;
        if (card.scrollIntoView) card.scrollIntoView({block: 'start', behavior: 'smooth'});
        card.focus({preventScroll: true});
    }, []);

    // `/alerts/rules#webhook` (el link que deja Plataformas cuando la tarjeta se muda acá, D11): la
    // tarjeta se dibuja recién con la lista, así que el navegador no encuentra el ancla al navegar.
    const handledHash = useRef(false);
    useEffect(() => {
        if (handledHash.current || !payload || window.location.hash !== '#webhook') return;
        handledHash.current = true;
        goToWebhook();
    }, [payload, goToWebhook]);

    // ------------------------------------------------------------------ dibujo -------------------

    // La primera fila que se dibuja lleva las anclas de la guía `aprende-alertas` (D7, como `guideRow` del Tablero): las
    // filas se arman en el orden en que quedan en la pantalla, así que la primera que pide es la primera de la lista.
    let guideRowTaken = false;
    const takeGuideRow = () => {
        const first = !guideRowTaken;
        guideRowTaken = true;
        return first;
    };

    const renderSystemRow = (rule) => (
        <SystemRuleRow key={rule.id} rule={rule} effects={effectsOf(rule)} anchorId={ruleAnchorId(rule.id)}
                       highlighted={highlightId === String(rule.id)} busy={isBusy(rule.id)} readOnly={!canEdit}
                       guideRow={takeGuideRow()} onToggle={toggle} onOpen={(item) => setPanelRuleId(item.id)}/>
    );

    const renderCustomRow = (rule) => (
        <CustomRuleRow key={rule.id} rule={rule} effects={effectsOf(rule)} anchorId={ruleAnchorId(rule.id)}
                       highlighted={highlightId === String(rule.id)} busy={isBusy(rule.id)} readOnly={!canEdit}
                       guideRow={takeGuideRow()} onToggle={toggle} onDelete={remove}/>
    );

    const renderToolbar = () => (
        <div className="alc-toolbar">
            <div className="uiseg alc-filters" role="tablist" aria-label={t('alerts_center.rules.filters_label')}>
                {RULE_FILTERS.map((key) => {
                    const active = key === filter;
                    const count = view.counts[key];
                    return (
                        <button key={key} type="button" role="tab" aria-selected={active}
                                className={`uiseg__btn${active ? ' -active' : ''}`}
                                disabled={!active && count === 0 && key !== FILTER_ALL}
                                onClick={() => changeFilter(key)}>
                            {t(FILTER_LABEL_KEYS[key])}
                            <span className="uiseg__count">{count}</span>
                        </button>
                    );
                })}
            </div>
            <label className="alc-search">
                <MagnifyingGlass size={15} aria-hidden="true"/>
                <input type="text" value={query} onChange={(event) => setQuery(event.target.value)}
                       placeholder={t('alerts_center.rules.search')} aria-label={t('alerts_center.rules.search')}/>
                {query && (
                    <button type="button" className="alc-search__clear" aria-label={t('alerts_center.rules.search_clear')}
                            onClick={() => setQuery('')}>
                        <X size={13} weight="bold" aria-hidden="true"/>
                    </button>
                )}
            </label>
        </div>
    );

    const renderCustomGroup = () => {
        const showGroup = view.custom.length > 0 || filter === FILTER_ALL || filter === FILTER_MINE;
        if (!showGroup) return null;
        const noneCreated = split.custom.length === 0;
        return (
            <section className="alc-group" aria-labelledby="alc-group-custom">
                <h2 id="alc-group-custom" className="alc-group__title">
                    {t('alerts_center.rules.group_custom')}
                    <span className="alc-group__count">{view.custom.length}</span>
                </h2>
                {view.custom.length > 0 && <div className="alc-group__rows">{view.custom.map(renderCustomRow)}</div>}
                {view.custom.length === 0 && noneCreated && (
                    <div className="alc-empty -custom">
                        <p>{t('alerts_center.rules.empty_custom')}</p>
                        {canEdit && (
                            <Link to={ALERTS_NEW} className="alc-btn -primary">
                                <Plus size={14} weight="bold"/>
                                {t('alerts_center.cta_new')}
                            </Link>
                        )}
                    </div>
                )}
                {view.custom.length === 0 && !noneCreated && (
                    <p className="alc-empty -inline">{t('alerts_center.rules.empty_custom_filtered')}</p>
                )}
            </section>
        );
    };

    const renderSystemGroups = () => {
        const known = new Set(SECTION_ORDER);
        const sections = [...SECTION_ORDER, 'OTHER'];
        return sections.map((section) => {
            const rows = view.system.filter((rule) => (section === 'OTHER'
                ? !known.has(rule.section) : rule.section === section));
            if (rows.length === 0) return null;
            const titleId = `alc-group-${section.toLowerCase()}`;
            return (
                <section key={section} className="alc-group" aria-labelledby={titleId}>
                    <h2 id={titleId} className="alc-group__title">
                        {t(`alerts_center.rules.section_${section.toLowerCase()}`)}
                        <span className="alc-group__count">{rows.length}</span>
                    </h2>
                    <div className="alc-group__rows">{rows.map(renderSystemRow)}</div>
                </section>
            );
        });
    };

    // El botón que abre y cierra un pliegue: el título con su número y, si hay, qué hacen las de adentro.
    const renderFoldHead = (key, title, count, hint = null) => {
        const open = isFoldOpen(key);
        return (
            <button type="button" className="alc-fold__head" aria-expanded={open} aria-controls={`alc-fold-body-${key}`}
                    onClick={() => toggleFold(key)}>
                {open ? <CaretDown size={13} weight="bold" aria-hidden="true"/> : <CaretRight size={13} weight="bold" aria-hidden="true"/>}
                <span className="alc-fold__title">{title}</span>
                <span className="alc-group__count">{count}</span>
                {hint && <span className="alc-fold__hint">{hint}</span>}
            </button>
        );
    };

    const renderEmailGroup = (rows) => (
        <section className="alc-group" aria-labelledby="alc-group-email" data-testid="alc-group-email">
            <h2 id="alc-group-email" className="alc-group__title">
                {t('alerts_center.rules.group_email')}
                <span className="alc-group__count">{rows.length}</span>
            </h2>
            {rows.length > 0
                ? <div className="alc-group__rows">{rows.map(renderSystemRow)}</div>
                : <p className="alc-empty -inline">{t('alerts_center.rules.group_email_empty')}</p>}
        </section>
    );

    const renderFold = ({key}, rows) => {
        if (rows.length === 0) return null;
        return (
            <section key={key} className={`alc-fold${isFoldOpen(key) ? ' -open' : ''}`} data-testid={`alc-fold-${key}`}>
                <h2 className="alc-fold__heading">
                    {renderFoldHead(key, t(`alerts_center.rules.fold_${key}`), rows.length, t(`alerts_center.rules.fold_${key}_hint`))}
                </h2>
                {isFoldOpen(key) && (
                    <div id={`alc-fold-body-${key}`} className="alc-group__rows alc-fold__body">{rows.map(renderSystemRow)}</div>
                )}
            </section>
        );
    };

    // «Todas»: lo propio y lo que manda correo a la vista; el resto de las de Dashbo, plegado por lo que hace.
    const renderDefaultGroups = () => {
        const system = bySection(view.system);
        return (
            <>
                {renderCustomGroup()}
                {renderEmailGroup(system.filter((rule) => usageOf(rule) === USE_EMAIL))}
                {FOLDS.map((fold) => renderFold(fold, system.filter((rule) => usageOf(rule) === fold.use)))}
            </>
        );
    };

    const renderCannotAlert = () => {
        if (cannotShown.length === 0) return null;
        const open = isFoldOpen(FOLD_CANNOT);
        return (
            <section className={`alc-fold alc-cannot${open ? ' -open' : ''}`} data-testid="alc-cannot-alert">
                <h2 className="alc-fold__heading">
                    {renderFoldHead(FOLD_CANNOT, t('alerts_center.rules.cannot_alert_title'), cannotShown.length,
                        t('alerts_center.rules.fold_cannot_hint'))}
                </h2>
                {open && (
                    <div id={`alc-fold-body-${FOLD_CANNOT}`} className="alc-cannot__body">
                        <p className="alc-cannot__reason">
                            <Trans i18nKey="alerts_center.rules.cannot_alert_reason"
                                   components={[<Link key="comparative" to={COMPARATIVE_METRICS_PATH} className="alc-link"/>]}/>
                        </p>
                        <ul className="alc-cannot__list">
                            {cannotShown.map((rule) => {
                                const {title, subtitle} = systemRuleTitle(rule, t);
                                return <li key={rule.id} id={ruleAnchorId(rule.id)}>{subtitle ? `${title} · ${subtitle}` : title}</li>;
                            })}
                        </ul>
                    </div>
                )}
            </section>
        );
    };

    const renderList = () => {
        if (loadError !== null) {
            return (
                <LoadErrorState title={loadError} text={t('alerts_center.load_error_explanation')}
                                retryLabel={t('alerts_center.retry')} onRetry={onRetry}/>
            );
        }
        if (loading && !payload) return <div className="box-loader"><Spinner color="positive"/></div>;
        // Una búsqueda que sólo encuentra algo en «No pueden alertar» no es «ninguna coincide».
        const nothing = view.custom.length === 0 && view.system.length === 0 && cannotMatches === 0;
        return (
            <>
                {!canEdit && <p className="alc-notice -warn" role="note" data-testid="alc-read-only">{t('alerts_center.no_edit_permission')}</p>}
                {renderToolbar()}
                {grouped ? renderDefaultGroups() : (
                    <>
                        {renderCustomGroup()}
                        {renderSystemGroups()}
                    </>
                )}
                {!grouped && nothing && (
                    <div className="alc-empty" data-testid="alc-empty-filter">
                        <p>{t('alerts_center.rules.empty_filter')}</p>
                        <button type="button" className="alc-btn -ghost"
                                onClick={() => { setQuery(''); changeFilter(FILTER_ALL); }}>
                            {t('alerts_center.rules.see_all')}
                        </button>
                    </div>
                )}
                {renderCannotAlert()}
            </>
        );
    };

    return (
        <div className="alc-rules" data-testid="alc-rules">
            <ChannelsCard emailCount={emailCount} userId={userId} webhook={webhook} onGoToWebhook={goToWebhook}/>
            {renderList()}
            <WebhookCardV2 webhook={webhook} systemRules={split.system} onSaved={onWebhookSaved}
                           onRetry={onWebhookRetry} readOnly={!canEdit}/>
            {panelRule && (
                <SystemRulePanel rule={panelRule} effects={effectsOf(panelRule)} busy={isBusy(panelRule.id)}
                                 readOnly={!canEdit} onToggle={toggle} onClose={closePanel}
                                 onSaved={(patch) => onPayloadChange((current) => replaceRuleIn(current, patch))}/>
            )}
        </div>
    );
}
